import { useState, useRef, useEffect } from 'react';
import Supercluster from 'supercluster';
import type { Feature as GeoJsonFeature, Point } from 'geojson'; // Import GeoJSON types

// Define PointProperties and ClusterProperties based on Supercluster's output
// Match this with PointProps in mapSelectors.ts if necessary
export interface PointProps { // This should ideally match the one in mapSelectors.ts
  id: string;
  name: string;
  locationType: string; // Keep as string if LocationType is complex, or import if simple
  address: string;
  averageRating?: number;
  ratingCount?: number;
  drinkCount?: number;
  lastSightingDate?: number;
  key?: string; // Add key property
  // Add other properties if they exist in the points passed to the hook
}

export interface ClusterProps {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string;
  key?: string; // Add key property
}

export type CombinedFeature = GeoJsonFeature<Point, PointProps | ClusterProps>;

// Define the input point type (must match output of selectVisibleGeoJsonPoints)
type InputPoint = GeoJsonFeature<Point, PointProps>;

// Define BBox type as per spec
type BoundingBox = [number, number, number, number]; // west, south, east, north

// Supercluster options from spec §4.4 (or common defaults)
const superclusterOptions: Supercluster.Options<PointProps, ClusterProps> = {
  radius: 150, // Cluster radius in pixels (spec §4.4 via §3.3 reference)
  maxZoom: 17, // Max zoom level to cluster the points (spec §4.4 via §3.3 reference)
  minZoom: 0, // Min zoom level to cluster the points
  minPoints: 2, // Minimum number of points required to form a cluster
  extent: 512, // Tile extent (standard)
  nodeSize: 64, // Size of the KD-tree node (standard)
  log: false, // Disable logging
  // Generate cluster properties
  reduce: (accumulated, props) => {
    accumulated.point_count += props.point_count ?? 1;
  },
  // Properties to initialize for a cluster
  map: () => ({
    cluster: true,
    cluster_id: -1, // Supercluster will assign a real ID
    point_count: 1,
    point_count_abbreviated: '', // Supercluster generates this if needed
  }),
};

/**
 * Custom hook to manage Supercluster instance and retrieve clusters/points.
 * @param points Array of GeoJSON points to cluster.
 * @param zoom Current map zoom level.
 * @param bounds Current map bounding box [west, south, east, north].
 * @returns Array of clusters and single points for the current view.
 */
const useSupercluster = (
  points: InputPoint[],
  zoom: number,
  bounds: BoundingBox | null
): CombinedFeature[] => {
  // State for the supercluster instance
  const supercluster = useRef<Supercluster<PointProps, ClusterProps> | null>(null);

  // Memoize the cluster instance based on points
  useEffect(() => {
    if (points.length > 0) {      
      const sc = new Supercluster(superclusterOptions);
      sc.load(points);
      supercluster.current = sc;
    } else {
      supercluster.current = null; // Clear instance if no points
    }
    // Re-run only when the identity of the points array changes
  }, [points]);

  // State for the clustered features
  const [clusters, setClusters] = useState<CombinedFeature[]>([]);

  // Refs to hold previous clusters for identity comparison
  const prevClusterMapRef = useRef<Map<string, CombinedFeature>>(new Map());
  const prevClustersArrayRef = useRef<CombinedFeature[]>([]);

  // Helper to build stable keys
  const buildFeatureKey = (feature: CombinedFeature): string => {
    const props = feature.properties;
    if ('cluster' in props && props.cluster) {
      // Cluster feature: Use rounded coordinates + point count
      const coords = feature.geometry.coordinates;
      const precision = 4; // Round to 4 decimal places
      const roundedLng = coords[0].toFixed(precision);
      const roundedLat = coords[1].toFixed(precision);
      return `c-${roundedLng}-${roundedLat}-${props.point_count}`;
    }
    // Point feature - include locationType in the key to force changes when type updates
    const pointProps = props as PointProps;
    return `p-${pointProps.id}-${pointProps.locationType || 'unknown'}`;
  };

  // Update clusters when bounds or zoom change
  useEffect(() => {
    if (!supercluster.current || !bounds) {
      setClusters([]); // Clear clusters if no instance or bounds
      return;
    }

    const currentZoom = Math.floor(zoom); // Use integer zoom for clustering

    try {
      const rawClusters = supercluster.current.getClusters(bounds, currentZoom) as CombinedFeature[];

      // Build new array with stable references where possible
      const previousFeatureMap = prevClusterMapRef.current; // Get map from previous render
      const nextMap: Map<string, CombinedFeature> = new Map();

      const stableClusters: CombinedFeature[] = rawClusters.map(feature => {
        const calculatedKey = buildFeatureKey(feature);
        // Assign the key to the properties object directly for later use
        feature.properties.key = calculatedKey;

        const prevFeature = previousFeatureMap.get(calculatedKey);
        let wasReused = false; // Logging flag

        if (prevFeature) {
          // Compare coordinates and, for clusters, point_count for equality
          const [lngPrev, latPrev] = (prevFeature.geometry.coordinates as number[]);
          const [lngNext, latNext] = (feature.geometry.coordinates as number[]);

          // Introduce a small tolerance for coordinate comparison
          const coordTolerance = 1e-4; // Adjust as needed (0.000001 degrees)
          const coordsAreSame = Math.abs(lngPrev - lngNext) < coordTolerance && Math.abs(latPrev - latNext) < coordTolerance;

          let isSame = coordsAreSame; // Start with coordinate check

          if ('cluster' in feature.properties && feature.properties.cluster) {
            isSame = isSame && feature.properties.point_count === (prevFeature.properties as ClusterProps).point_count;
          } else {
            // For non-cluster points, also check critical properties like locationType
            const newProps = feature.properties as PointProps;
            const prevProps = prevFeature.properties as PointProps;
            
            // Check if critical properties have changed
            const propertiesSame = 
              newProps.locationType === prevProps.locationType &&
              newProps.name === prevProps.name &&
              newProps.averageRating === prevProps.averageRating;
              
            if (!propertiesSame) {
            }
            
            isSame = isSame && propertiesSame;
          }

          if (isSame) {
            nextMap.set(calculatedKey, prevFeature);
            wasReused = true; // Mark as reused
            return prevFeature; // Reuse previous object reference
          }
        }

        if (!wasReused) {
        } else {
        }

        // Use new feature and store in map
        nextMap.set(calculatedKey, feature);
        return feature;
      });

      // Determine if array identity can be preserved
      let finalClusters: CombinedFeature[];

      if (previousFeatureMap.size !== nextMap.size) {
        // Size changed, definitely different
        finalClusters = stableClusters;
      } else {
        // Sizes are the same, check references for each key
        let contentChanged = false;
        
        // Check if any feature references have changed
        for (const [key, nextFeature] of nextMap.entries()) {
          const prevFeature = previousFeatureMap.get(key);
          if (!prevFeature || prevFeature !== nextFeature) {
            contentChanged = true;
            
            // If it's a point, log the specific property changes
            if (!('cluster' in nextFeature.properties)) {
              const oldProps = prevFeature ? prevFeature.properties as PointProps : null;
              
              if (oldProps) {
              }
            }
            break;
          }
        }

        if (contentChanged) {
          finalClusters = stableClusters; // Use the newly computed array
        } else {
          // Content is identical, reuse the PREVIOUS array reference
          finalClusters = prevClustersArrayRef.current; 
        }
      }

      // Log reference stability

      // Update refs for next comparison
      prevClusterMapRef.current = nextMap;
      prevClustersArrayRef.current = finalClusters;

      setClusters(finalClusters);
    } catch (error) {
      console.error('[useSupercluster] Error getting clusters:', error);
      setClusters([]); // Clear clusters on error
    }

    // Dependency array: update when points identity, bounds value, or zoom value changes.
    // Note: Comparing bounds array by value requires careful handling if bounds object identity is not stable.
    // Stringifying bounds for the dependency array is a common workaround.
  }, [points, zoom, bounds ? JSON.stringify(bounds) : 'null']); // Use stringified bounds

  return clusters;
};

export default useSupercluster; 